import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dashboard from './pages/DashboardV2';
import Klienci from './pages/Klienci';
import KlientDetail from './pages/KlientDetailV2';
import Spacery from './pages/SpaceryV2';
import SpacerDetail from './pages/SpacerDetail';
import Finanse from './pages/FinanseV2';
import Faktury from './pages/FakturyV2';
import Ustawienia from './pages/UstawieniaV2';
import Kalendarz from './pages/Kalendarz';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import WaitingVerify from './pages/WaitingVerify';
import AdminPanel from './pages/AdminPanel';
import Czat from './pages/Czat';
import AdminCzat from './pages/AdminCzat';
import KlientPortal, { detectKlientSlug } from './pages/KlientPortal';
import { IconGrid, IconUsers, IconBuilding, IconChart, IconSettings, IconCalendar, IconDoc } from './components/Icons';
import InstallPrompt from './components/InstallPrompt';
import WelcomeModal from './components/WelcomeModal';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/ConfirmDialog';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { HintsProvider } from './contexts/HintsContext';

function AppInner() {
  const { user, loading, logout, stopImpersonating, isImpersonating } = useAuth();
  const { t, i18n } = useTranslation();
  // Sprawdź tokeny w URL
  const urlParams   = new URLSearchParams(window.location.search);
  const resetToken  = urlParams.get('reset');
  const verifyToken = urlParams.get('verify');
  const adminTab    = urlParams.get('admin'); // np. ?admin=czat z maila powiadomienia
  const klientParam = urlParams.get('klient'); // np. ?klient=12 — powrót z podglądu panelu klienta
  const wantRegister = urlParams.get('register'); // np. ?register=1 — CTA z landingu (otwarta rejestracja)
  const [authView, setAuthView] = useState(
    verifyToken ? 'verify' : resetToken ? 'reset' : wantRegister ? 'register' : 'login'
  );
  const [view, setView] = useState(
    klientParam ? 'klient' : adminTab === 'czat' ? 'czat' : 'dashboard'
  );
  const [klientId, setKlientId] = useState(klientParam ? Number(klientParam) : null);
  const [spacerId, setSpacerId] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [czatBadge, setCzatBadge] = useState(0);
  const [pendingAction, setPendingAction] = useState(null);

  const NAV = [
    { id: 'dashboard',  label: t('nav.dashboard'),  Icon: IconGrid },
    { id: 'klienci',    label: t('nav.clients'),    Icon: IconUsers },
    { id: 'spacery',    label: t('nav.spaces'),     Icon: IconBuilding },
    { id: 'kalendarz',  label: t('nav.calendar'),   Icon: IconCalendar },
    { id: 'faktury',    label: t('nav.invoices'),   Icon: IconDoc },
    { id: 'finanse',    label: t('nav.finances'),   Icon: IconChart },
  ];

  // Pobierz liczbę nieprzeczytanych wiadomości co 20s
  // - regular user: nieprzeczytane od admina
  // - admin: łączna liczba nieprzeczytanych od wszystkich userów
  useEffect(() => {
    if (!user) return;
    const fetchBadge = () => {
      import('./api').then(({ api }) => {
        const fn = user.is_admin ? api.adminCzatNieprzeczytane : api.czatNieprzeczytane;
        fn().then(r => setCzatBadge(r.count)).catch(() => {});
      });
    };
    fetchBadge();
    const id = setInterval(fetchBadge, 20000);
    return () => clearInterval(id);
  }, [user]);

  // Po skonsumowaniu ?klient=<id> (powrót z podglądu panelu) usuń parametr z URL,
  // żeby odświeżenie strony nie wracało na siłę do tego klienta.
  useEffect(() => {
    if (!klientParam) return;
    const u = new URL(window.location.href);
    u.searchParams.delete('klient');
    window.history.replaceState(null, '', u.pathname + u.search + u.hash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Po zalogowaniu zastosuj język zapisany na koncie — o ile w URL nie ma jawnego ?lang
  // (świeży handoff z landingu ma priorytet nad zapisaną preferencją).
  useEffect(() => {
    if (!user?.lang) return;
    if (urlParams.get('lang')) return;
    if (user.lang !== i18n.language) {
      i18n.changeLanguage(user.lang);
      localStorage.setItem('sp_lang', user.lang);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Loading spinner
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid var(--border)',
            borderTopColor: 'var(--acc)',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
            margin: '0 auto 12px',
          }} />
          <p style={{ fontSize: '11px', color: 'var(--ink3)' }}>{t('app.loading')}</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Not authenticated
  if (!user) {
    if (authView === 'register')
      return <Register onNavigateLogin={() => setAuthView('login')} />;
    if (authView === 'forgot')
      return <ForgotPassword onBack={() => setAuthView('login')} />;
    if (authView === 'reset')
      return <ResetPassword token={resetToken} onSuccess={() => setAuthView('login')} />;
    if (authView === 'verify')
      return <VerifyEmail token={verifyToken} onSuccess={() => setAuthView('login')} />;
    return <Login onNavigateRegister={() => setAuthView('register')} onNavigateForgot={() => setAuthView('forgot')} />;
  }

  // Email niezweryfikowany — gate
  if (user.email_verified === 0) {
    if (authView === 'verify')
      return <VerifyEmail token={verifyToken} onSuccess={() => window.location.reload()} />;
    return <WaitingVerify />;
  }

  // Authenticated app
  const navigate = (page, id, options) => {
    setView(page);
    if (page === 'spacer') setSpacerId(id);
    else if (id) setKlientId(id);
    setMenuOpen(false);
    setPendingAction(options?.action || null);
  };

  const consumeAction = () => setPendingAction(null);

  const renderPage = () => {
    switch (view) {
      case 'dashboard': return <Dashboard onNavigate={navigate} />;
      case 'klienci':   return <Klienci onNavigate={navigate} initialAction={pendingAction} onActionConsumed={consumeAction} />;
      case 'klient':    return <KlientDetail klientId={klientId} onNavigate={navigate} initialAction={pendingAction} onActionConsumed={consumeAction} />;
      case 'spacery':   return <Spacery onNavigate={navigate} initialAction={pendingAction} onActionConsumed={consumeAction} />;
      case 'spacer':    return <SpacerDetail spacerId={spacerId} onNavigate={navigate} />;
      case 'kalendarz':  return <Kalendarz onNavigate={navigate} />;
      case 'faktury':    return <Faktury onNavigate={navigate} />;
      case 'finanse':    return <Finanse onNavigate={navigate} />;
      case 'czat':       return user.is_admin ? <AdminCzat /> : <Czat />;
      case 'ustawienia': return <Ustawienia />;
      case 'admin':      return user.is_admin ? <AdminPanel /> : <Dashboard onNavigate={navigate} />;
      default:           return <Dashboard onNavigate={navigate} />;
    }
  };

  const activeNav = view === 'klient' ? 'klienci' : view === 'spacer' ? 'spacery' : view;

  // Powitalny modal przy pierwszym logowaniu — tylko dla nowych fotografów
  // (admin i impersonacja są pomijane, żeby modal nie sygnalizował welcome_seen u testowanego konta).
  const showWelcome = user.welcome_seen === false && !user.is_admin && !isImpersonating;

  return (
    <div className="dark min-h-screen text-white flex" style={{ overflowX: 'hidden', maxWidth: '100vw' }}>
      {showWelcome && <WelcomeModal onNavigate={navigate} />}
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-56 flex flex-col transform transition-transform duration-200 ${menuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
        style={{
          background: 'var(--bg-nav)',
          borderRight: '1px solid var(--border)',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}>
        <div style={{ borderBottom: '1px solid var(--border)', padding: '18px 20px' }}>
          <h1 className="font-black tracking-tight" style={{ fontSize: '15px', color: 'var(--acc2)' }}>ScanPilots</h1>
          <p className="mt-0.5" style={{ fontSize: '10px', color: 'var(--ink3)' }}>{t('app.tagline')}</p>
        </div>
        <nav className="flex-1 px-2 py-3 flex flex-col gap-0.5">
          {NAV.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => navigate(id)}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-all ${
                activeNav === id
                  ? 'nav-active'
                  : 'rounded-btn hover:bg-navy-800'
              }`}
              style={activeNav !== id ? { color: 'var(--ink2)' } : {}}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {label}
            </button>
          ))}
        </nav>
        {/* Czat + Ustawienia + Admin — nad info użytkownika */}
        <div style={{ padding: '4px 8px' }}>
          <button
              onClick={() => { navigate('czat'); if (!user.is_admin) setCzatBadge(0); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-all ${
                activeNav === 'czat' ? 'nav-active' : 'rounded-btn hover:bg-navy-800'
              }`}
              style={activeNav !== 'czat' ? { color: 'var(--ink2)' } : {}}
            >
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
              <span style={{ flex: 1, textAlign: 'left' }}>{t('nav.support')}</span>
              {czatBadge > 0 && (
                <span style={{
                  minWidth: '16px', height: '16px', borderRadius: '8px',
                  background: 'var(--acc)', color: '#fff',
                  fontSize: '9px', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 4px',
                }}>
                  {czatBadge}
                </span>
              )}
            </button>
          <button
            onClick={() => navigate('ustawienia')}
            className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-all ${
              activeNav === 'ustawienia' ? 'nav-active' : 'rounded-btn hover:bg-navy-800'
            }`}
            style={activeNav !== 'ustawienia' ? { color: 'var(--ink2)' } : {}}
          >
            <IconSettings className="w-4 h-4 flex-shrink-0" />
            {t('nav.settings')}
          </button>
          {user.is_admin === 1 && (
            <button
              onClick={() => navigate('admin')}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium transition-all ${
                activeNav === 'admin' ? 'nav-active' : 'rounded-btn hover:bg-navy-800'
              }`}
              style={activeNav !== 'admin' ? { color: 'var(--acc)' } : {}}
            >
              <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
              {t('nav.admin')}
            </button>
          )}
        </div>

        {/* User info + logout */}
        <div style={{ borderTop: '1px solid var(--border)', padding: '12px 16px' }}>
          <div style={{ marginBottom: '8px' }}>
            <p style={{ fontSize: '11px', fontWeight: 600, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.imie || user.email}
            </p>
            {user.imie && (
              <p style={{ fontSize: '9px', color: 'var(--ink3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {user.email}
              </p>
            )}
          </div>
          <button
            onClick={logout}
            style={{
              width: '100%',
              background: 'var(--bg4)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--ink3)',
              fontSize: '9px',
              fontWeight: 700,
              letterSpacing: '0.04em',
              padding: '5px 10px',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.target.style.color = 'var(--err2)'}
            onMouseLeave={e => e.target.style.color = 'var(--ink3)'}
          >
            {t('app.logout')}
          </button>
        </div>
      </aside>

      {menuOpen && (
        <div className="fixed inset-0 z-30 bg-black/60 md:hidden" onClick={() => setMenuOpen(false)} />
      )}

      <div className="flex-1 md:ml-56 flex flex-col min-h-screen" style={{ minWidth: 0, maxWidth: '100%' }}>
        <header className="md:hidden flex items-center justify-between px-4 sticky top-0 z-20"
          style={{
            background: 'var(--bg-nav)',
            borderBottom: '1px solid var(--border)',
            paddingTop: 'calc(12px + env(safe-area-inset-top))',
            paddingBottom: '12px',
            paddingLeft: 'calc(16px + env(safe-area-inset-left))',
            paddingRight: 'calc(16px + env(safe-area-inset-right))',
          }}>
          <button onClick={() => setMenuOpen(true)} className="text-lg leading-none" style={{ color: 'var(--ink2)' }}>&#9776;</button>
          <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--acc2)' }}>ScanPilots</span>
          <button
            onClick={logout}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--ink3)',
              fontSize: '9px',
              fontWeight: 700,
              cursor: 'pointer',
              letterSpacing: '0.04em',
            }}
          >
            {t('app.logout')}
          </button>
        </header>
{isImpersonating && (
          <div style={{
            background: '#7c3aed',
            borderBottom: '1px solid #6d28d9',
            padding: '8px 20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '12px',
            color: '#fff',
          }}>
            <span>
              Przeglądasz konto: <strong>{user?.email}</strong>
            </span>
            <button
              onClick={stopImpersonating}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '5px',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
                padding: '4px 12px',
                cursor: 'pointer',
              }}
            >
              ← Wróć do admina
            </button>
          </div>
        )}
        <main className="flex-1 p-4 md:p-6 max-w-6xl w-full mx-auto" style={{ minWidth: 0 }}>
          {renderPage()}
        </main>
      </div>
      <InstallPrompt />
    </div>
  );
}

export default function App() {
  // Klient portal — wykrycie po hostname/path. Wcześniej niż providery panelu fotografa,
  // żeby uniknąć ładowania AuthContext (osobny token w klientApi).
  const klientSlug = detectKlientSlug();
  if (klientSlug) {
    return (
      <ThemeProvider>
        <KlientPortal slug={klientSlug} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <HintsProvider>
        <ToastProvider>
          <ConfirmProvider>
            <AuthProvider>
              <AppInner />
            </AuthProvider>
          </ConfirmProvider>
        </ToastProvider>
      </HintsProvider>
    </ThemeProvider>
  );
}
